import {
  AdChannel,
  MetaObjective,
  OptimizationGoal,
} from "~/ui-rtk/api/types/custom-attribution-enums"
import { ChannelSelector } from "./ChannelSelector"
import { ObjectiveSelector } from "./ObjectiveSelector"
import { GoalSelector } from "./GoalSelector"
import { ChevronLeftIcon } from "@heroicons/react/24/outline"

interface ConfigurationStepProps {
  adChannel: AdChannel | null
  adChannelObjective: MetaObjective | null
  adChannelOptimizationGoal: OptimizationGoal | null
  onChannelSelect: (channel: AdChannel | null) => void
  onObjectiveSelect: (objective: MetaObjective | null) => void
  onGoalSelect: (goal: OptimizationGoal | null) => void
  onBack?: () => void
  isEditMode?: boolean
  errors?: {
    adChannel?: string
    adChannelObjective?: string
    adChannelOptimizationGoal?: string
  }
  touched?: {
    adChannel?: boolean
    adChannelObjective?: boolean
    adChannelOptimizationGoal?: boolean
  }
  adsetData?: Record<string, string | undefined>[]
}

const BackButton = ({
  onClick,
  children,
}: {
  onClick: () => void
  children: React.ReactNode
}) => (
  <div className="px-system-md py-system-sm">
    <button
      type="button"
      onClick={onClick}
      className="text-system-blue-400 p-0 hover:text-system-blue-300 text-base flex items-center gap-1 rounded-md hover:bg-system-blue-500/10 transition-colors"
    >
      <ChevronLeftIcon className="w-4 h-4" />
      {children}
    </button>
  </div>
)

export function ConfigurationStep({
  adChannel,
  adChannelObjective,
  adChannelOptimizationGoal,
  onChannelSelect,
  onObjectiveSelect,
  onGoalSelect,
  onBack,
  isEditMode = false,
  errors,
  touched,
  adsetData = [],
}: ConfigurationStepProps) {
  // Meta: objective + goal. TikTok / Pinterest / Google Ads / YouTube: goal only.
  const configComplete =
    adChannel &&
    ((adChannel === AdChannel.META &&
      adChannelObjective &&
      adChannelOptimizationGoal) ||
      ((adChannel === AdChannel.TIKTOK ||
        adChannel === AdChannel.PINTEREST ||
        adChannel === AdChannel.GOOGLE_ADS ||
        adChannel === AdChannel.YOUTUBE) &&
        adChannelOptimizationGoal))

  return (
    <div className="">
      {/* Breadcrumb Navigation */}
      <div className="px-system-md py-system-sm bg-system-blue-600/10 border-b border-system-blue-600/20">
        <div className="flex items-center gap-2 text-lg text-system-gray-400">
          <span className="font-medium">Channels</span>
          {adChannel && (
            <>
              <span className="text-system-gray-500">›</span>
              <span className="text-white font-medium">
                {adChannel.replace(/_/g, " ").toLowerCase()}
              </span>
            </>
          )}
          {adChannelObjective && adChannel === AdChannel.META && (
            <>
              <span className="text-system-gray-500">›</span>
              <span className="text-white font-medium">
                {adChannelObjective.replace(/_/g, " ").toLowerCase()}
              </span>
            </>
          )}
          {adChannelOptimizationGoal && (
            <>
              <span className="text-system-gray-500">›</span>
              <span className="text-white font-medium">
                {adChannelOptimizationGoal.replace(/_/g, " ").toLowerCase()}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Back Buttons - Hidden in edit mode */}
      {!isEditMode && (
        <>
          {/* Back Button - Only when configuration is complete */}
          {configComplete && onBack && (
            <BackButton onClick={onBack}>Back to Previous Step</BackButton>
          )}

          {/* Back Button - For TikTok / Pinterest / Google Ads / YouTube Goal Selection (Level 2b) */}
          {(adChannel === AdChannel.TIKTOK ||
            adChannel === AdChannel.PINTEREST ||
            adChannel === AdChannel.GOOGLE_ADS ||
            adChannel === AdChannel.YOUTUBE) &&
            !adChannelOptimizationGoal && (
              <BackButton
                onClick={() => {
                  onChannelSelect(null)
                  onGoalSelect(null)
                }}
              >
                Back to Channels
              </BackButton>
            )}

          {/* Back Button - For Meta Objective Selection (Level 2a) */}
          {adChannel === AdChannel.META && !adChannelObjective && (
            <BackButton
              onClick={() => {
                onChannelSelect(null)
                onObjectiveSelect(null)
                onGoalSelect(null)
              }}
            >
              Back to Channels
            </BackButton>
          )}

          {/* Back Button - For Meta Goal Selection (Level 3) */}
          {adChannel === AdChannel.META &&
            adChannelObjective &&
            !adChannelOptimizationGoal && (
              <BackButton
                onClick={() => {
                  onObjectiveSelect(null)
                  onGoalSelect(null)
                }}
              >
                Back to Objectives
              </BackButton>
            )}
        </>
      )}

      {/* File Explorer Content */}
      <div>
        {/* <AnimatePresence mode="wait"> */}
        {/* Level 1: Channel Selection */}
        {!adChannel && (
          <ChannelSelector
            onChannelSelect={onChannelSelect}
            error={errors?.adChannel}
            touched={touched?.adChannel}
          />
        )}

        {/* Level 2a: Objective Selection (Meta only) */}
        {adChannel === AdChannel.META && !adChannelObjective && (
          <ObjectiveSelector
            adChannel={adChannel}
            onObjectiveSelect={onObjectiveSelect}
            onGoalSelect={onGoalSelect}
            error={errors?.adChannelObjective}
            touched={touched?.adChannelObjective}
            adsetData={adsetData}
          />
        )}

        {/* Level 2b: Goal Selection (TikTok / Pinterest / Google Ads / YouTube) */}
        {(adChannel === AdChannel.TIKTOK ||
          adChannel === AdChannel.PINTEREST ||
          adChannel === AdChannel.GOOGLE_ADS ||
          adChannel === AdChannel.YOUTUBE) &&
          !adChannelOptimizationGoal && (
            <GoalSelector
              adChannel={adChannel}
              onGoalSelect={onGoalSelect}
              error={errors?.adChannelOptimizationGoal}
              touched={touched?.adChannelOptimizationGoal}
              adsetData={adsetData}
            />
          )}

        {/* Level 3: Goal Selection (Meta only) */}
        {adChannel === AdChannel.META &&
          adChannelObjective &&
          !adChannelOptimizationGoal && (
            <GoalSelector
              objective={adChannelObjective}
              adChannel={adChannel}
              onGoalSelect={onGoalSelect}
              error={errors?.adChannelOptimizationGoal}
              touched={touched?.adChannelOptimizationGoal}
              adsetData={adsetData}
            />
          )}
        {/* </AnimatePresence> */}
      </div>
    </div>
  )
}
